// Player de gravação compartilhado pelas telas de detalhe e auditoria.
// O <audio> nativo fica oculto; os controles seguem o visual do produto.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import "../i18n.js";

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function AudioPlayer({
  url,
  loading,
  downloadUrl,
  downloadable = true,
}: {
  url: string | null;
  loading: boolean;
  downloadUrl?: string | null;
  downloadable?: boolean;
}) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
  }, [url]);

  if (loading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={t("audioPlayer.loading")}
        className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3"
      >
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <Skeleton className="h-2 flex-1 rounded-full" />
        <Skeleton className="h-4 w-10" />
      </div>
    );
  }

  if (!url) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
        {t("audioPlayer.unavailable")}
      </div>
    );
  }

  function toggle() {
    const element = audioRef.current;
    if (!element) return;
    if (element.paused) void element.play();
    else element.pause();
  }

  function seek(next: number) {
    const element = audioRef.current;
    if (!element || !duration) return;
    element.currentTime = next;
    setCurrent(element.currentTime);
  }

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <Button
        type="button"
        size="icon"
        onClick={toggle}
        aria-label={playing ? t("audioPlayer.pause") : t("audioPlayer.play")}
        className="size-10 shrink-0 rounded-full"
      >
        {playing ? <Pause className="size-4" /> : <Play className="size-4 translate-x-px" />}
      </Button>

      <div className="group relative h-2 flex-1 rounded-full bg-border">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary opacity-0 shadow transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
          style={{ left: `${progress}%` }}
        />
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(current, duration || 0)}
          aria-label={t("audioPlayer.position")}
          onChange={(event) => seek(Number(event.currentTarget.value))}
          className="absolute -inset-y-3 left-0 w-full cursor-pointer opacity-0"
        />
      </div>

      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {clock(current)} / {clock(duration)}
      </span>

      {downloadable && (
        <a
          href={downloadUrl ?? url}
          download
          aria-label={t("audioPlayer.download")}
          className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          <Download className="size-4" />
        </a>
      )}
    </div>
  );
}
